/**
 * Delete confirmation for posts, blogs and courses that may be published.
 *
 *   if (!(await confirmDeleteContent({ kind: "post", ids: [postId] }))) return;
 *   const res = await axios.post(...delete...);
 *   showDeleteResult(res.data);
 *
 * The dialog asks the server where the items are published now (LinkedIn, X, Facebook,
 * YouTube, profile page) and tells the user they will be removed from there too.
 * The delete endpoints do the removal (api/services/content_delete.py).
 *
 * onlyIfPublished: for pages that already asked "Are you sure?" in their own modal —
 * the dialog is then shown only when something is published.
 */
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getCookie } from "./storageUtils";

export type DeleteKind = "post" | "blog" | "course";

interface Place { platform: string; label: string; removable: boolean; url?: string; }
interface Preview { items: { id: string; title: string; published: Place[] }[]; published_on: Place[]; is_published: boolean; }

const NOUN: Record<DeleteKind, [string, string]> = {
  post: ["post", "posts"], blog: ["blog", "blogs"], course: ["course", "courses"],
};

const apiUrl = () => process.env.REACT_APP_SERVER_ENDPOINT || "";
const userEmail = () => localStorage.getItem("user_email") || getCookie("user_email") || "";

export async function fetchDeletePreview(kind: DeleteKind, ids: string[]): Promise<Preview | null> {
  try {
    const res = await axios.get(`${apiUrl()}/api/socialmedia/api/content/delete_preview`, {
      params: { kind, ids: ids.join(","), user_email: userEmail() || undefined },
    });
    return res.data as Preview;
  } catch (e) {
    console.warn("[delete] preview failed", e);
    return null;
  }
}

const ConfirmDialog: React.FC<{
  kind: DeleteKind; ids: string[]; preview: Preview | null; onDone: (ok: boolean) => void;
}> = ({ kind, ids, preview, onDone }) => {
  const [open, setOpen] = useState(true);
  const close = (ok: boolean) => { setOpen(false); onDone(ok); };
  const n = ids.length;
  const noun = n === 1 ? NOUN[kind][0] : NOUN[kind][1];
  const places = preview?.published_on || [];
  const social = places.filter((p) => p.platform !== "authority");
  const onProfile = places.some((p) => p.platform === "authority");
  const manual = places.filter((p) => !p.removable);

  return (
    <Dialog open={open} onClose={() => close(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
        <WarningAmberIcon sx={{ color: "#F59E0B" }} />
        Delete {n === 1 ? `this ${noun}` : `${n} ${noun}`}?
      </DialogTitle>
      <DialogContent>
        {preview === null ? (
          <Typography sx={{ fontSize: 14 }}>
            This can't be undone. If it was published, it will also be removed from the social media
            accounts and the profile page it was published to.
          </Typography>
        ) : places.length ? (
          <>
            <Typography sx={{ fontSize: 14, mb: 1 }}>
              {n === 1 ? `This ${noun} is` : `These ${noun} are`} published. Deleting will also remove it from:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3, mb: 1 }}>
              {social.filter((p) => p.removable).map((p) => (
                <li key={p.platform}><Typography sx={{ fontSize: 14, fontWeight: 600 }}>{p.label}</Typography></li>
              ))}
              {onProfile && (
                <li><Typography sx={{ fontSize: 14, fontWeight: 600 }}>Your public profile page</Typography></li>
              )}
            </Box>
            {manual.length > 0 && (
              <Typography sx={{ fontSize: 13, color: "#B45309", mb: 1 }}>
                {manual.map((p) => p.label).join(", ")} can't be removed automatically — you'll get a link
                to delete it there yourself.
              </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>This can't be undone.</Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 14 }}>
            {n === 1 ? `This ${noun} isn't` : `None of these ${noun} are`} published anywhere. This can't be undone.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => close(false)} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" color="error" onClick={() => close(true)} sx={{ textTransform: "none" }}>
          {places.length ? "Delete everywhere" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Ask the user; resolves true when they confirm. */
export async function confirmDeleteContent(opts: {
  kind: DeleteKind; ids: string[]; onlyIfPublished?: boolean;
}): Promise<boolean> {
  const ids = (opts.ids || []).filter(Boolean).map(String);
  if (!ids.length) return false;

  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = ReactDOM.createRoot(host);
  const cleanup = () => setTimeout(() => { root.unmount(); host.remove(); }, 300);

  // small spinner while the server checks where it is published
  root.render(
    <Dialog open maxWidth="xs">
      <DialogContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={20} />
        <Typography sx={{ fontSize: 14 }}>Checking where it is published…</Typography>
      </DialogContent>
    </Dialog>
  );
  const preview = await fetchDeletePreview(opts.kind, ids);

  if (opts.onlyIfPublished && preview && !preview.is_published) {
    cleanup();
    return true;
  }
  return new Promise<boolean>((resolve) => {
    root.render(<ConfirmDialog kind={opts.kind} ids={ids} preview={preview}
      onDone={(ok) => { cleanup(); resolve(ok); }} />);
  });
}

/**
 * After a delete: a toast with where it was removed from. Anything that is still online
 * (YouTube, a platform that is no longer connected, a platform error) is shown in a dialog
 * with links, and the promise resolves when the user closes it — so callers that reload
 * the page should `await` this first.
 */
export async function showDeleteResult(data: any, fallback = "Deleted successfully"): Promise<void> {
  const removed = (data?.removed_from || []).map((r: any) => r.label).filter(Boolean);
  toast.success(removed.length ? `${fallback}. Also removed from ${removed.join(", ")}.` : fallback);
  const still: any[] = data?.still_online || [];
  if (!still.length) return;

  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = ReactDOM.createRoot(host);
  await new Promise<void>((resolve) => {
    const Done: React.FC = () => {
      const [open, setOpen] = useState(true);
      useEffect(() => { if (!open) resolve(); }, [open]);
      return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
            <WarningAmberIcon sx={{ color: "#F59E0B" }} /> Deleted — but still online here
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, mb: 1 }}>
              These copies could not be removed automatically. Open each link and delete it there:
            </Typography>
            {still.map((s, i) => (
              <Box key={i} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {s.url ? <a href={s.url} target="_blank" rel="noreferrer">{s.label}</a> : s.label}
                  {s.title ? ` — ${s.title}` : ""}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{s.reason}</Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="contained" onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>OK</Button>
          </DialogActions>
        </Dialog>
      );
    };
    root.render(<Done />);
  });
  setTimeout(() => { root.unmount(); host.remove(); }, 300);
}
